import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const C = {
  gold: "#D4A843", blue: "#4A90D9", green: "#43B581", red: "#E74C3C",
  bg: "#0D0F13", card: "#13161C", border: "#1E222A",
  white: "#F0F0F0", whiteGray: "#A0A8B8", gray: "#6B7280", purple: "#9B59B6",
};

const tierColor = (t) => ({ 1: C.blue, 2: C.gold, 3: C.green, 4: "#00D4AA", 5: C.purple }[t] || C.gray);

function StatBlock({ label, value, color, sub }) {
  return (
    <div data-testid={`pulse-stat-${label.toLowerCase().replace(/\s/g, "-")}`} style={{
      flex: "1 1 130px", minWidth: 130,
      background: C.bg, border: `1px solid ${color}25`, borderRadius: 10,
      padding: "10px 14px",
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 10, color: C.gray, letterSpacing: 1, fontFamily: "monospace", marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: C.whiteGray, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function LessonRow({ row, type }) {
  const isFail = type === "fail";
  const accent = isFail ? C.red : C.green;
  const value = isFail ? `${row.fails}/${row.attempts}` : `${row.passes}`;
  const right = isFail ? `${Math.round((row.fail_rate || 0) * 100)}%` : `${row.unique_users}u`;
  return (
    <div data-testid={`pulse-${type}-row-${row.lesson_id}`} style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "6px 10px", borderRadius: 6, background: `${accent}08`,
      border: `1px solid ${accent}20`, marginBottom: 4, fontSize: 11,
    }}>
      <span style={{ color: C.white, fontFamily: "monospace", fontSize: 11 }}>{row.lesson_id}</span>
      <span style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ color: accent, fontWeight: 700 }}>{value}</span>
        <span style={{ color: C.gray, fontSize: 10 }}>{right}</span>
      </span>
    </div>
  );
}

export default function CohortPulse() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const intervalRef = useRef(null);

  const fetchPulse = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/admin/cohort-pulse`, ax);
      setData(d);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.status === 403 ? "No 'cohort' permission" : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPulse();
    intervalRef.current = setInterval(fetchPulse, 60_000);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [fetchPulse]);

  if (loading) {
    return (
      <div data-testid="cohort-pulse-loading" style={{
        background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
        padding: "16px 20px", margin: "0 24px 16px", color: C.gray, fontSize: 12,
      }}>Loading Cohort Pulse…</div>
    );
  }

  if (err) {
    return (
      <div data-testid="cohort-pulse-error" style={{
        background: C.card, border: `1px solid ${C.red}30`, borderRadius: 12,
        padding: "16px 20px", margin: "0 24px 16px", color: C.red, fontSize: 12,
      }}>Cohort Pulse: {err}</div>
    );
  }

  const tg = data?.tier_grads_24h || {};

  return (
    <div data-testid="cohort-pulse" style={{
      background: C.card, border: `1px solid ${C.border}`, borderRadius: 12,
      padding: "16px 20px", margin: "0 24px 16px",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{
            width: 8, height: 8, borderRadius: "50%", background: C.green,
            boxShadow: `0 0 10px ${C.green}`, animation: "pulse-dot 1.6s ease-in-out infinite",
          }} />
          <span style={{ fontSize: 11, letterSpacing: 2, color: C.gold, fontFamily: "monospace" }}>
            COHORT PULSE — LAST 24h
          </span>
        </div>
        <button data-testid="pulse-refresh-btn" onClick={fetchPulse}
          style={{
            background: "transparent", border: `1px solid ${C.border}`, color: C.gray,
            padding: "4px 10px", borderRadius: 6, fontSize: 11, cursor: "pointer",
          }}>↻ Refresh</button>
      </div>

      {/* Top stat row */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <StatBlock label="Active Users" value={data.active_users_24h} color={C.blue} />
        <StatBlock label="Lessons Passed" value={data.unique_completions_24h} color={C.green}
          sub={`${data.passes_24h}/${data.attempts_24h} attempts`} />
        <StatBlock label="Tier Grads" value={data.total_tier_grads_24h} color={C.gold}
          sub={Object.entries(tg).map(([k, v]) => `${k.replace("tier", "T")}:${v}`).join(" • ") || "—"} />
        <StatBlock label="Pass Rate"
          value={data.attempts_24h ? `${Math.round((data.passes_24h / data.attempts_24h) * 100)}%` : "—"}
          color={C.purple} />
      </div>

      {/* Hot/Failing lessons + recent grads */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <div data-testid="pulse-hot-lessons">
          <div style={{ fontSize: 10, letterSpacing: 1, color: C.green, fontFamily: "monospace", marginBottom: 6 }}>
            🔥 HOTTEST LESSONS
          </div>
          {(data.top_completed_lessons || []).length === 0 ? (
            <div style={{ color: C.gray, fontSize: 11, padding: 6 }}>No completions yet</div>
          ) : (
            data.top_completed_lessons.map(r => <LessonRow key={r.lesson_id} row={r} type="pass" />)
          )}
        </div>

        <div data-testid="pulse-failing-lessons">
          <div style={{ fontSize: 10, letterSpacing: 1, color: C.red, fontFamily: "monospace", marginBottom: 6 }}>
            ⚠ STUCK LESSONS
          </div>
          {(data.top_failing_lessons || []).length === 0 ? (
            <div style={{ color: C.gray, fontSize: 11, padding: 6 }}>No failures — clean cohort 🎯</div>
          ) : (
            data.top_failing_lessons.map(r => <LessonRow key={r.lesson_id} row={r} type="fail" />)
          )}
        </div>

        <div data-testid="pulse-recent-grads">
          <div style={{ fontSize: 10, letterSpacing: 1, color: C.gold, fontFamily: "monospace", marginBottom: 6 }}>
            🎓 RECENT GRADS
          </div>
          {(data.recent_grads || []).length === 0 ? (
            <div style={{ color: C.gray, fontSize: 11, padding: 6 }}>No grads in window</div>
          ) : (
            data.recent_grads.map((g, i) => (
              <div key={i} data-testid={`pulse-grad-${i}`} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "6px 10px", borderRadius: 6,
                background: `${tierColor(g.tier)}08`, border: `1px solid ${tierColor(g.tier)}25`,
                marginBottom: 4, fontSize: 11,
              }}>
                <span style={{ color: C.white }}>{g.user_name}</span>
                <span style={{ color: tierColor(g.tier), fontWeight: 700, fontSize: 10, fontFamily: "monospace" }}>
                  T{g.tier}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 9, color: C.gray, textAlign: "right", fontFamily: "monospace" }}>
        Updated {data.generated_at ? new Date(data.generated_at).toLocaleTimeString() : "—"} • auto-refresh 60s
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.75); }
        }
      `}</style>
    </div>
  );
}
